import { Box, Button, TextField, Typography, MenuItem } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useFormik } from "formik";
import { useContext, useState } from "react";
import InputMask from "react-input-mask";
import { useLocation, useNavigate } from "react-router-dom";
import Select from "react-select";
import * as yup from "yup";
import Layout from "../../components/layout/Layout";
import "./AddCaregiverPage.css";
import { ClientContext } from "../../state/ClientContext";
import { US_STATES } from "../../util/defs";
import { apiRequest } from "../../api/baseApi";
import LeaveWithoutSavingModal from "../../components/LeaveWithoutSavingModal";

const AddCaregiverPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false);
  const { refetchCaregivers } = useContext(ClientContext);

  const isFormForAdmin = location.pathname.includes("/add-admin");

  const responsibilityOptions = [
    { label: "Assisted Living Center", value: "Assisted Living Renter" },
    { label: "Behavioral Health", value: "Behavioral Health" },
    { label: "Psychiatric Professional", value: "Psychiatric Professional" },
    { label: "Group Home", value: "Group Home" },
    { label: "DO/MD Office", value: "DO/MD Office" },
    { label: "Aging Office", value: "Aging Office" },
    {
      label: "Certified Nursing Assistant",
      value: "Certified Nursing Assistant",
    },
    { label: "Licensed Practical Nurse", value: "Licensed Practical Nurse" },
    { label: "Registered Nurse", value: "Registered Nurse" },
    {
      label: "Physical or Occupational Therapist",
      value: "Physical or Occupational Therapist",
    },
    { label: "Friend or Family Member", value: "Friend or Family Member" },
    { label: "Speech Therapist", value: "Speech Therapist" },
    { label: "Support Coordinator", value: "Support Coordinator" },
    { label: "House Manager", value: "House Manager" },
    { label: "Other", value: "Other" },
  ];

  const schemaParams = {
    firstName: yup.string().required("Enter first name."),
    lastName: yup.string().required("Enter last name."),
    email: yup.string().email("Enter a valid email.").required("Enter email."),
    phone: yup
      .string()
      .matches(/^\+1\d{10}$/, "Phone number must be in the format +1XXXXXXXXXX")
      .required("Enter valid U.S phone number."),
    title: yup.string().required("Enter title."),
    responsibility: yup.string().required("Enter responsibility."),
    company: yup.string().required("Enter company name."),
    city: yup
      .string()
      .matches(
        /^[a-zA-Z ]*$/,
        "City can’t contain any special characters or numbers."
      )
      .required("Enter city."),
    state: yup.string().required("Enter state."),
    zipCode: yup
      .string()
      .matches(/^\d{5}$/, "Zip code must be 5 digits.")
      .required("Enter zip code."),
  };

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      title: "",
      email: "",
      phone: "",
      company: "",
      responsibility: "",
      city: "",
      state: "",
      zipCode: "",
    },
    validationSchema: yup.object(schemaParams),
    onSubmit: async (values) => {
      const userType = isFormForAdmin ? "caregiver_admin" : "caregiver";

      try {
        const response = await apiRequest("/users/invite", {
          version: "v1",
          method: "POST",
          body: {
            name: values.firstName + " " + values.lastName,
            first_name: values.firstName,
            last_name: values.lastName,
            email: values.email,
            user_type: userType,
            title: values.title,
            company: values.company,
            city: values.city,
            state: values.state,
            phone: values.phone,
            zip_code: values.zipCode,
            responsibility: values.responsibility,
          },
        });

        const data = await response.json();

        if (response.ok) {
          refetchCaregivers();
          navigate("/invite-sent", { state: { user: data.user } });
        } else if (data.message === "User already created") {
          formik.setFieldError(
            "email",
            "This email is already assigned to another caregiver. Enter a different email."
          );
        } else {
          console.error("Error inviting: " + data.message);
        }
      } catch (error) {
        console.error("Error inviting: " + error.message);
      }
    },
  });

  const onInputChangeResponsibility = (newValue) => {
    formik.setFieldValue("responsibility", newValue.value);
  };

  return (
    <Layout isTouched={formik.dirty} showSideNav={false}>
      <div className="alerts-page-container">
        <div className="user-page-heading">
          <h1>Add new {isFormForAdmin ? "admin" : "caregiver"}</h1>
          <p>
            Invite the caregiver to register their Compass Care account. After
            registering, you can assign them to clients.
          </p>
        </div>
        <div className="add-client-container">
          <Box
            component="form"
            sx={{ "& > :not(style)": { m: 1, width: "100%" } }}
            noValidate
            autoComplete="off"
            onSubmit={formik.handleSubmit}
          >
            <h2 className="form-heading">
              <strong>Caregiver Details</strong>
            </h2>
            <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Grid item size={4}>
                <label htmlFor="firstName">First Name</label>
              </Grid>
              <Grid item size={8}>
                <TextField
                  id="firstName"
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.firstName && Boolean(formik.errors.firstName)
                  }
                  helperText={
                    formik.touched.firstName && formik.errors.firstName
                  }
                  fullWidth
                  required
                />
              </Grid>
            </Grid>
            <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Grid item size={4}>
                <label htmlFor="lastName">Last Name</label>
              </Grid>
              <Grid item size={8}>
                <TextField
                  type="text"
                  id="lastName"
                  value={formik.values.lastName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.lastName && Boolean(formik.errors.lastName)
                  }
                  helperText={formik.touched.lastName && formik.errors.lastName}
                  fullWidth
                  required
                />
              </Grid>
            </Grid>
            <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Grid item size={4}>
                <label htmlFor="title">Title</label>
              </Grid>
              <Grid item size={8}>
                <TextField
                  type="title"
                  id="title"
                  value={formik.values.title}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.title && Boolean(formik.errors.title)}
                  helperText={formik.touched.title && formik.errors.title}
                  fullWidth
                  required
                />
              </Grid>
            </Grid>
            <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Grid item size={4}>
                <label htmlFor="company">Company Name</label>
              </Grid>
              <Grid item size={8}>
                <TextField
                  type="company"
                  id="company"
                  value={formik.values.company}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.company && Boolean(formik.errors.company)
                  }
                  helperText={formik.touched.company && formik.errors.company}
                  fullWidth
                  required
                />
              </Grid>
            </Grid>
            <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Grid item size={4}>
                <label htmlFor="responsibility">Responsibility</label>
              </Grid>
              <Grid item size={8}>
                <Select
                  defaultValue={[]}
                  name="responsibility"
                  onChange={onInputChangeResponsibility}
                  options={responsibilityOptions}
                  placeholder="Select caregiver responsibility"
                  classNamePrefix="select"
                />
                {formik.errors.responsibility && (
                  <div
                    style={{
                      fontSize: "0.75rem",
                      color: "#d32f2f",
                      paddingLeft: "15px",
                      textAlign: "left",
                    }}
                  >
                    {formik.errors.responsibility}
                  </div>
                )}
              </Grid>
            </Grid>
            <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Grid item size={4}>
                <label htmlFor="email">Email</label>
              </Grid>
              <Grid item size={8}>
                <TextField
                  id="email"
                  type="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                  fullWidth
                  required
                />
              </Grid>
            </Grid>
            <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Grid item size={4}>
                <label htmlFor="phone">Phone Number</label>
              </Grid>
              <Grid item size={8}>
                <InputMask
                  mask="+19999999999"
                  value={formik.values.phone}
                  disabled={false}
                  maskChar=" "
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  {(inputProps) => (
                    <TextField
                      {...inputProps}
                      id="phone"
                      type="tel"
                      placeholder="+1XXXXXXXXXX"
                      value={formik.values.phone}
                      error={
                        formik.touched.phone && Boolean(formik.errors.phone)
                      }
                      helperText={formik.touched.phone && formik.errors.phone}
                      fullWidth
                      required
                    />
                  )}
                </InputMask>
              </Grid>
            </Grid>
            <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Grid item size={4}>
                <label htmlFor="city">City</label>
              </Grid>
              <Grid item size={8}>
                <TextField
                  type="city"
                  id="city"
                  value={formik.values.city}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.city && Boolean(formik.errors.city)}
                  helperText={formik.touched.city && formik.errors.city}
                  fullWidth
                  required
                />
              </Grid>
            </Grid>
            <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Grid item size={4}>
                <label htmlFor="state">State</label>
              </Grid>
              <Grid item size={8}>
                <TextField
                  id="state"
                  name="state"
                  select
                  value={formik.values.state}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.state && Boolean(formik.errors.state)}
                  helperText={formik.touched.state && formik.errors.state}
                  fullWidth
                  required
                >
                  {US_STATES.map((stateName) => (
                    <MenuItem key={stateName} value={stateName}>
                      {stateName}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
            <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Grid item size={4}>
                <label htmlFor="zipCode">ZIP code</label>
              </Grid>
              <Grid item size={8}>
                <TextField
                  id="zipCode"
                  type="text"
                  value={formik.values.zipCode}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.zipCode && Boolean(formik.errors.zipCode)
                  }
                  helperText={formik.touched.zipCode && formik.errors.zipCode}
                  fullWidth
                  required
                />
              </Grid>
            </Grid>
          </Box>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box></Box>
            <Box display="flex" gap={2}>
              <Button
                variant="outlined"
                className="activity-action-btn"
                onClick={() => {
                  if (formik.dirty) {
                    setIsLeaveModalOpen(true);
                  } else {
                    navigate(-1);
                  }
                }}
              >
                <Typography textTransform={"capitalize"}>Cancel</Typography>
              </Button>
              <Button
                variant="outlined"
                className="activity-action-btn"
                onClick={formik.handleSubmit}
              >
                <Typography textTransform={"capitalize"}>Save</Typography>
              </Button>
            </Box>
          </Box>
        </div>
      </div>

      <LeaveWithoutSavingModal
        isOpen={isLeaveModalOpen}
        handleClose={() => setIsLeaveModalOpen(false)}
        handleLeave={() => navigate(-1)}
      />
    </Layout>
  );
};

export default AddCaregiverPage;
